'use client'

import { motion } from 'framer-motion'
import { FallbackImg } from '@/components/pattern-search/fallback-image'

export interface GalleryImage {
  src: string
  alt: string
  patternId?: number
  url: string
  designer: string
}

interface ScrollingGalleryProps {
  images: GalleryImage[]
  speed?: number // seconds for one complete scroll cycle
  direction?: 'left' | 'right'
  imageHeight?: number
  gap?: number
  staggerOffset?: number // vertical offset for alternate images (for interlocking rows)
  invertStagger?: boolean // if true, even indices are raised instead of odd
  className?: string
}

export function ScrollingGallery({
  images,
  speed = 40,
  direction = 'left',
  imageHeight = 200,
  gap = 12,
  staggerOffset = 50,
  invertStagger = false,
  className = '',
}: ScrollingGalleryProps) {

  // Duplicate images to create seamless loop
  const duplicatedImages = [...images, ...images]

  // Aspect ratio 0.9 (width:height) - matches PatternCell
  const imageWidth = imageHeight * 0.9
  const totalWidth = images.length * (imageWidth + gap)

  // Container needs extra height to accommodate stagger offset
  const containerHeight = imageHeight + staggerOffset

  return (
    <div className={`overflow-hidden ${className}`} style={{ height: containerHeight }}>
      <motion.div
        className="flex items-start"
        style={{ gap }}
        animate={{
          x: direction === 'left' ? [0, -totalWidth] : [-totalWidth, 0],
        }}
        transition={{
          x: {
            duration: speed,
            repeat: Infinity,
            ease: 'linear',
            repeatType: 'loop',
          },
        }}
      >
        {duplicatedImages.map((image, index) => (
          <div
            key={`${image.src}-${index}`}
            className="flex-shrink-0 relative box-border rounded-sm border border-gray-300 overflow-hidden bg-gray-100 shadow-md shadow-black/40"
            style={{
              width: imageWidth,
              height: imageHeight,
              marginTop: (invertStagger ? index % 2 === 0 : index % 2 === 1) ? staggerOffset : 0,
            }}
          >
            <FallbackImg
              src={image.src}
              alt={image.alt}
              className="absolute inset-0 w-full h-full"
              draggable={false}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1">
              <span className="text-white text-xs font-medium truncate block">by {image.designer}</span>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  )
}

interface StackedScrollingGalleryProps {
  images: GalleryImage[]
  speed?: number
  imageHeight?: number
  horizontalGap?: number
  verticalGap?: number
  staggerOffset?: number // vertical offset for alternate columns
  className?: string
}

export function StackedScrollingGallery({
  images,
  speed = 40,
  imageHeight = 200,
  horizontalGap = 12,
  verticalGap = 12,
  staggerOffset = 50,
  className = '',
}: StackedScrollingGalleryProps) {

  // Pair up images for stacking (2 per column)
  const pairedImages: [GalleryImage, GalleryImage | undefined][] = []
  for (let i = 0; i < images.length; i += 2) {
    pairedImages.push([images[i], images[i + 1]])
  }

  // Duplicate pairs for seamless loop
  const duplicatedPairs = [...pairedImages, ...pairedImages]

  const imageWidth = imageHeight * 0.9
  const totalWidth = pairedImages.length * (imageWidth + horizontalGap)
  const containerHeight = imageHeight * 2 + verticalGap + staggerOffset

  return (
    <div className={`overflow-hidden ${className}`} style={{ height: containerHeight }}>
      <motion.div
        className="flex items-start"
        style={{ gap: horizontalGap }}
        animate={{
          x: [0, -totalWidth],
        }}
        transition={{
          x: {
            duration: speed,
            repeat: Infinity,
            ease: 'linear',
            repeatType: 'loop',
          },
        }}
      >
        {duplicatedPairs.map((pair, pairIndex) => (
          <div
            key={`pair-${pairIndex}`}
            className="flex-shrink-0 flex flex-col"
            style={{
              gap: verticalGap,
              marginTop: pairIndex % 2 === 1 ? staggerOffset : 0,
            }}
          >
            {pair.map((image, imageIndex) =>
              image ? (
                <div
                  key={`${image.src}-${pairIndex}-${imageIndex}`}
                  className="relative box-border rounded-sm border border-gray-300 overflow-hidden bg-gray-100 shadow-md shadow-black/40"
                  style={{
                    width: imageWidth,
                    height: imageHeight,
                  }}
                >
                  <FallbackImg
                    src={image.src}
                    alt={image.alt}
                    className="absolute inset-0 w-full h-full"
                    draggable={false}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1">
                    <span className="text-white text-xs font-medium truncate block">by {image.designer}</span>
                  </div>
                </div>
              ) : null
            )}
          </div>
        ))}
      </motion.div>
    </div>
  )
}
